import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_CERTIFICATE_SETTINGS, mergeCertificateSettings } from '../utils/certificateUtils';
import { DEFAULT_INVITATION_LETTER, DEFAULT_EMPLOYER_INVITATION_LETTER, mergeAllBodySections, mergeEmployerBodySections } from '../utils/invitationLetterUtils';
import { DEFAULT_PACKAGES_SETTINGS } from '../constants/defaultPackages';
import { DEFAULT_ACCOMMODATION_SETTINGS } from '../constants/defaultAccommodation';
import {
  formatConferenceRangeLong,
  formatConferenceRangeReceipt,
  formatConferenceRangeShort,
  formatConferenceRangeWithLocation,
  getConferenceDayLabels,
  getLastNovemberConferenceDates,
  inferConferenceYear,
} from '../utils/conferenceDateFormat';
import {
  readSettingsLocalCache,
  writeSettingsLocalCache,
  CONFERENCE_SETTINGS_CACHE_KEY,
} from '../utils/settingsLocalCache';

const CONFERENCE_YEAR = 2026;
const DEFAULT_NOVEMBER_DATES = getLastNovemberConferenceDates(CONFERENCE_YEAR);

const SETTINGS_DOC = 'conference_settings';
const CURRENT_ID = 'current';

export const DEFAULT_ABSTRACT_NOTIFY_EMAILS = [
  'febby.kahabuka@gmail.com',
  'tanzaniadentalassociation01@gmail.com',
  'muyabinmg@gmail.com',
];

export const DEFAULT_ADMIN_NOTIFY_EMAILS = [
  'tanzaniadentalassociation01@gmail.com',
  'muyabinmg@gmail.com',
];

export const DEFAULT_CONTACT = {
  email: 'info@tdaconference2026.org',
  phone: '+255 758 960 550',
  whatsapp: '+255758960550',
  sponsorship_email: 'sponsorship@tdaconference2026.org',
  accommodation_email: 'accommodation@tdaconference2026.org',
  accommodation_phone: '+255 758 960 550',
  wohd_email: 'wohd@tdaconference2026.org',
  website: 'www.tdaevents.or.tz',
  org_name: 'Tanzania Dental Association',
};

export const DEFAULT_REGISTRATION_FEES = {
  member: { early: 150000, regular: 200000, label: 'Member' },
  'non-member': { early: 250000, regular: 300000, label: 'Non-Member' },
  student: { early: 75000, regular: 100000, label: 'Student' },
  'east-africa-delegate': { early: 175000, regular: 225000, label: 'East Africa Delegate' },
  exhibitor: { early: 500000, regular: 600000, label: 'Exhibitor' },
  supporter: { early: 1000000, regular: 1000000, label: 'Supporter' },
};

export const DEFAULT_CONFERENCE_SETTINGS = {
  hero_title: '41st Tanzania Dental Association',
  admin_notify_emails: [...DEFAULT_ADMIN_NOTIFY_EMAILS],
  hero_subtitle: 'Scientific Conference & AGM 2026',
  conference_start: DEFAULT_NOVEMBER_DATES?.start || '2026-11-25',
  conference_end: DEFAULT_NOVEMBER_DATES?.end || '2026-11-27',
  location: 'Katavi Region, Tanzania',
  about_text:
    'Join us for the 41st Tanzania Dental Association Scientific Conference & AGM 2026 in the beautiful Katavi Region. This premier dental event brings together dental professionals, researchers, and industry leaders from across Tanzania and East Africa.',
  early_bird_deadline: '2026-09-26',
  abstract_deadline: '2026-10-30',
  registration_deadline: '2026-11-15',
  registration_fees: DEFAULT_REGISTRATION_FEES,
  abstract_settings: {
    call_title: 'Call for Abstracts',
    intro_paragraph_1:
      'The Tanzania Dental Association (TDA) will hold its 41st Scientific Conference and Annual General Meeting from 25th to 27th November, 2026 in Katavi.',
    intro_paragraph_2:
      'We invite all oral health professionals to present their latest innovations, research and clinical insights driving the advancement of restorative dental care in Tanzania and beyond.',
    intro_paragraph_3:
      'This is a premier platform to showcase and share your valuable experiences.',
    invite_text: 'CONFERENCE ABSTRACTS ARE INVITED.',
    deadline_text: '30th OCTOBER, 2026',
    guidelines:
      'Submit abstracts in English using the structured form. Scientific abstracts require: Title, Background, Aim, Materials and method, Results, and Conclusion. Educational abstracts require: Title, Background, Body text, Conclusion, and Recommendation.',
    notify_emails: [...DEFAULT_ABSTRACT_NOTIFY_EMAILS],
  },
  theme: {
    title: 'Advancing Dental Practice in East Africa',
    description: '',
  },
  venue: {
    name: '',
    address: '',
    city: 'Katavi',
    country: 'Tanzania',
    map_url: '',
  },
  invitation_letter: DEFAULT_INVITATION_LETTER,
  employer_invitation_letter: DEFAULT_EMPLOYER_INVITATION_LETTER,
  certificate_settings: DEFAULT_CERTIFICATE_SETTINGS,
  contact: { ...DEFAULT_CONTACT },
  packages_settings: DEFAULT_PACKAGES_SETTINGS,
  accommodation_settings: DEFAULT_ACCOMMODATION_SETTINGS,
};

let cachedSettings = null;
let cacheLoadedAt = 0;
const CACHE_TTL_MS = 60 * 1000;

const mergeSettings = (data) => ({
  ...DEFAULT_CONFERENCE_SETTINGS,
  ...data,
  registration_fees: {
    ...DEFAULT_CONFERENCE_SETTINGS.registration_fees,
    ...(data?.registration_fees || {}),
  },
  abstract_settings: {
    ...DEFAULT_CONFERENCE_SETTINGS.abstract_settings,
    ...(data?.abstract_settings || {}),
  },
  theme: { ...DEFAULT_CONFERENCE_SETTINGS.theme, ...(data?.theme || {}) },
  venue: { ...DEFAULT_CONFERENCE_SETTINGS.venue, ...(data?.venue || {}) },
  invitation_letter: {
    ...DEFAULT_CONFERENCE_SETTINGS.invitation_letter,
    ...(data?.invitation_letter || {}),
    fees: data?.invitation_letter?.fees?.length
      ? data.invitation_letter.fees
      : DEFAULT_CONFERENCE_SETTINGS.invitation_letter.fees,
    bodySections: mergeAllBodySections(data?.invitation_letter?.bodySections),
  },
  employer_invitation_letter: {
    ...DEFAULT_CONFERENCE_SETTINGS.employer_invitation_letter,
    ...(data?.employer_invitation_letter || {}),
    fees: data?.employer_invitation_letter?.fees?.length
      ? data.employer_invitation_letter.fees
      : DEFAULT_CONFERENCE_SETTINGS.employer_invitation_letter.fees,
    bodySections: {
      sw: mergeEmployerBodySections(data?.employer_invitation_letter?.bodySections),
    },
  },
  certificate_settings: mergeCertificateSettings(data?.certificate_settings),
  contact: {
    ...DEFAULT_CONFERENCE_SETTINGS.contact,
    ...(data?.contact || {}),
  },
  packages_settings: {
    ...DEFAULT_PACKAGES_SETTINGS,
    ...(data?.packages_settings || {}),
    supporter_packages: data?.packages_settings?.supporter_packages?.length
      ? data.packages_settings.supporter_packages
      : DEFAULT_PACKAGES_SETTINGS.supporter_packages,
    exhibitor_packages: data?.packages_settings?.exhibitor_packages?.length
      ? data.packages_settings.exhibitor_packages
      : DEFAULT_PACKAGES_SETTINGS.exhibitor_packages,
    advertising_packages: data?.packages_settings?.advertising_packages?.length
      ? data.packages_settings.advertising_packages
      : DEFAULT_PACKAGES_SETTINGS.advertising_packages,
  },
  accommodation_settings: {
    ...DEFAULT_ACCOMMODATION_SETTINGS,
    ...(data?.accommodation_settings || {}),
    hotels: data?.accommodation_settings?.hotels?.length
      ? data.accommodation_settings.hotels
      : DEFAULT_ACCOMMODATION_SETTINGS.hotels,
    transportation: {
      ...DEFAULT_ACCOMMODATION_SETTINGS.transportation,
      ...(data?.accommodation_settings?.transportation || {}),
    },
  },
});

export const getPackagesSettings = (settings = getConferenceSettingsSync()) =>
  mergeSettings(settings).packages_settings;

export const getAccommodationSettings = (settings = getConferenceSettingsSync()) =>
  mergeSettings(settings).accommodation_settings;

export const getConferenceContact = (settings = getConferenceSettingsSync()) => ({
  ...DEFAULT_CONTACT,
  ...(settings.contact || {}),
});

export const getConferenceEventBranding = (settings = getConferenceSettingsSync()) => {
  const contact = getConferenceContact(settings);
  const heroTitle = settings.hero_title || DEFAULT_CONFERENCE_SETTINGS.hero_title;
  const heroSubtitle = settings.hero_subtitle || DEFAULT_CONFERENCE_SETTINGS.hero_subtitle;
  return {
    heroTitle,
    heroSubtitle,
    receiptTitle: heroSubtitle,
    certificateParticipationLine: `has participated in the ${heroTitle}`,
    ...contact,
  };
};

export const getConferenceSettingsSync = () =>
  cachedSettings ? mergeSettings(cachedSettings) : mergeSettings(DEFAULT_CONFERENCE_SETTINGS);

export const getConferenceSettings = async ({ forceRefresh = false } = {}) => {
  const now = Date.now();
  if (!forceRefresh && cachedSettings && now - cacheLoadedAt < CACHE_TTL_MS) {
    return mergeSettings(cachedSettings);
  }

  if (!forceRefresh) {
    const localCached = readSettingsLocalCache(CONFERENCE_SETTINGS_CACHE_KEY);
    if (localCached) {
      cachedSettings = localCached;
      cacheLoadedAt = now;
      return mergeSettings(cachedSettings);
    }
  }

  try {
    const snap = await getDoc(doc(db, SETTINGS_DOC, CURRENT_ID));
    if (snap.exists()) {
      cachedSettings = snap.data();
      cacheLoadedAt = now;
      writeSettingsLocalCache(CONFERENCE_SETTINGS_CACHE_KEY, cachedSettings);
      return mergeSettings(cachedSettings);
    }
  } catch (error) {
    console.error('Error loading conference settings:', error);
  }

  return mergeSettings(DEFAULT_CONFERENCE_SETTINGS);
};

export const upsertConferenceSettings = async (settings) => {
  const payload = {
    ...mergeSettings(settings),
    updated_at: serverTimestamp(),
  };
  await setDoc(doc(db, SETTINGS_DOC, CURRENT_ID), payload, { merge: true });
  cachedSettings = payload;
  cacheLoadedAt = Date.now();
  writeSettingsLocalCache(CONFERENCE_SETTINGS_CACHE_KEY, payload);
  return mergeSettings(payload);
};

export const getConferenceDateLabels = (settings = getConferenceSettingsSync()) => {
  const { conference_start: start, conference_end: end, location } = settings;
  return {
    short: formatConferenceRangeShort(start, end),
    long: formatConferenceRangeLong(start, end),
    withLocation: formatConferenceRangeWithLocation(start, end, location),
    receipt: formatConferenceRangeReceipt(start, end, location),
    dayLabels: getConferenceDayLabels(start, end),
    location: location || DEFAULT_CONFERENCE_SETTINGS.location,
  };
};

export const applyLastNovemberWeekdays = (settings, year = inferConferenceYear(settings)) => {
  const dates = getLastNovemberConferenceDates(year);
  if (!dates) return settings;

  return syncConferenceDatesInSettings({
    ...settings,
    conference_start: dates.start,
    conference_end: dates.end,
  });
};

export { getLastNovemberConferenceDates, inferConferenceYear };

const OUTDATED_INTRO_PATTERNS = [
  /26th to 28th November, 2026/gi,
  /26–28 November 2026/g,
  /26-28 November 2026/g,
];

export const syncConferenceDatesInSettings = (settings) => {
  const next = { ...settings };
  const longRange = formatConferenceRangeLong(
    settings.conference_start,
    settings.conference_end
  );
  const location = settings.location || DEFAULT_CONFERENCE_SETTINGS.location;

  if (next.abstract_settings?.intro_paragraph_1) {
    let intro = next.abstract_settings.intro_paragraph_1;
    OUTDATED_INTRO_PATTERNS.forEach((pattern) => {
      intro = intro.replace(pattern, longRange);
    });
    if (intro !== next.abstract_settings.intro_paragraph_1) {
      next.abstract_settings = { ...next.abstract_settings, intro_paragraph_1: intro };
    }
  } else if (!next.abstract_settings?.intro_paragraph_1 && longRange) {
    next.abstract_settings = {
      ...next.abstract_settings,
      intro_paragraph_1: `The Tanzania Dental Association (TDA) will hold its 41st Scientific Conference and Annual General Meeting from ${longRange} in ${location.split(',')[0] || location}.`,
    };
  }

  return next;
};

export const getRegistrationFees = () => getConferenceSettingsSync().registration_fees;

export const isEarlyBirdActive = (settings = getConferenceSettingsSync()) => {
  const deadline = settings.early_bird_deadline || DEFAULT_CONFERENCE_SETTINGS.early_bird_deadline;
  const end = new Date(`${deadline}T23:59:59+03:00`);
  return new Date() <= end;
};

export const isRegistrationOpen = (settings = getConferenceSettingsSync()) => {
  const deadline = settings.registration_deadline || DEFAULT_CONFERENCE_SETTINGS.registration_deadline;
  if (!deadline) return true;
  const end = new Date(`${deadline}T23:59:59+03:00`);
  return new Date() <= end;
};

export const getRegistrationDeadlineLabel = (settings = getConferenceSettingsSync()) => {
  const deadline = settings.registration_deadline || DEFAULT_CONFERENCE_SETTINGS.registration_deadline;
  if (!deadline) return '';
  return new Date(`${deadline}T12:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const assertRegistrationOpen = async () => {
  const settings = await getConferenceSettings();
  if (!isRegistrationOpen(settings)) {
    throw new Error(
      `Conference registration closed on ${getRegistrationDeadlineLabel(settings)}.`
    );
  }
};

export const isAbstractSubmissionOpen = (settings = getConferenceSettingsSync()) => {
  const deadline = settings.abstract_deadline || DEFAULT_CONFERENCE_SETTINGS.abstract_deadline;
  if (!deadline) return true;
  const end = new Date(`${deadline}T23:59:59+03:00`);
  return new Date() <= end;
};

export const getAbstractDeadlineLabel = (settings = getConferenceSettingsSync()) => {
  const custom = settings.abstract_settings?.deadline_text;
  if (custom) return custom;
  const deadline = settings.abstract_deadline || DEFAULT_CONFERENCE_SETTINGS.abstract_deadline;
  if (!deadline) return '';
  return new Date(`${deadline}T12:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const assertAbstractSubmissionOpen = async () => {
  const settings = await getConferenceSettings();
  if (!isAbstractSubmissionOpen(settings)) {
    throw new Error(
      `Abstract submission closed. The deadline was ${getAbstractDeadlineLabel(settings)}.`
    );
  }
};

export const calculateFeeForCategory = (category, earlyBird, fees = getRegistrationFees()) => {
  const categoryKey = String(category || '').toLowerCase().replace(/\s+/g, '-');
  const priceInfo = fees[categoryKey];
  if (!priceInfo) {
    throw new Error(`Invalid registration category: ${category}`);
  }
  return earlyBird ? Number(priceInfo.early) : Number(priceInfo.regular);
};

export const parseNotifyEmailList = (text) =>
  String(text || '')
    .split(/[,;\n]+/)
    .map((email) => email.trim())
    .filter(Boolean);

export const formatNotifyEmailList = (emails) =>
  Array.isArray(emails) ? emails.join('\n') : '';
